import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLanguage, fmtEUR } from "@/i18n";
import { api } from "@/api";
import { Reveal, Overline } from "@/components/motion";

export const CampaignCard = ({ campaign: c, index }) => {
  const { lang, clang, cf, t } = useLanguage();
  const pct = c.goal_amount > 0 ? Math.min(100, Math.round((c.raised_amount / c.goal_amount) * 100)) : 0;
  const past = c.status === "past";
  return (
    <Reveal delay={(index % 3) * 0.1} className="h-full">
      <Link
        to={`/campaigns/${c.slug || c.id}`}
        data-testid={`campaign-card-${c.id}`}
        className={`group flex flex-col h-full border border-forest/15 bg-cream overflow-hidden hover:-translate-y-2 transition-transform duration-500 ${past ? "opacity-80" : ""}`}
      >
        <div className="relative aspect-[4/3] overflow-hidden">
          <img src={c.image_url} alt={cf(c, "title")} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          {past && (
            <span className="absolute top-4 left-4 bg-forest text-cream text-[10px] uppercase tracking-[0.2em] font-semibold px-4 py-1.5 rounded-full" data-testid={`campaign-completed-badge-${c.id}`}>
              {t("campaignsPage.completed")}
            </span>
          )}
        </div>
        <div className="p-8 flex flex-col flex-1">
          <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-clay">{c.category}</span>
          <h3 className="mt-2 font-serif text-2xl md:text-3xl text-forest group-hover:text-clay transition-colors duration-300">{cf(c, "title")}</h3>
          <p className="mt-3 text-sm text-sage leading-relaxed line-clamp-3 flex-1">{cf(c, "summary")}</p>
          <div className="mt-6">
            <div className="h-1 bg-forest/10 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${past ? "bg-moss" : "bg-clay"}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-forest font-medium" data-testid={`campaign-progress-${c.id}`}>
                {fmtEUR(c.raised_amount, lang)} / {fmtEUR(c.goal_amount, lang)} · {pct}%
              </p>
              <ArrowUpRight size={18} className="text-clay group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
            </div>
          </div>
        </div>
      </Link>
    </Reveal>
  );
};

const Campaigns = () => {
  const { t } = useLanguage();
  const [campaigns, setCampaigns] = useState(null);

  useEffect(() => {
    api.get("/campaigns").then((r) => setCampaigns(r.data)).catch(() => setCampaigns([]));
  }, []);

  const active = (campaigns || []).filter((c) => c.status === "active");
  const past = (campaigns || []).filter((c) => c.status === "past");

  return (
    <div data-testid="campaigns-page" className="pt-24">
      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pt-16 pb-20">
        <Reveal>
          <Overline>{t("campaignsPage.overline")}</Overline>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-forest max-w-4xl" data-testid="campaigns-title">
            {t("campaignsPage.title")}
          </h1>
          <p className="mt-8 text-base lg:text-lg text-sage max-w-xl">{t("campaignsPage.sub")}</p>
        </Reveal>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 md:px-12 pb-24" data-testid="current-campaigns">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl text-forest mb-10 flex items-center gap-4">
            <span className="w-3 h-3 rounded-full bg-clay inline-block" />
            {t("campaignsPage.current")}
          </h2>
        </Reveal>
        {!campaigns ? (
          <p className="text-sage" data-testid="campaigns-loading">{t("common.loading")}</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {active.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
          </div>
        )}
      </section>

      <section className="bg-sand/60 border-t border-forest/10" data-testid="past-campaigns">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 py-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl text-forest mb-10 flex items-center gap-4">
              <span className="w-3 h-3 rounded-full bg-moss inline-block" />
              {t("campaignsPage.past")}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {past.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Campaigns;
